/**
 * CreditEngineEvents — /admin/credit-engine/events
 *
 * Wave 5-Late Credit Engine ANCHOR. Executive discipline inherited
 * BYTE-EXACT from the approved Beta / Marketplace / Analytics anchors:
 *   • AdminLayout wrap (already present).
 *   • Local `Section` primitive with `--ds-color-admin-border` hairline
 *     chrome + `box-shadow: 0 1px 0 0 admin-border`, header
 *     `px-4 py-2.5` divider with icon + `Heading`.
 *   • `--ds-radius-input` (3px) on inputs / selects / buttons, dense
 *     `px-2.5 py-1.5`.
 *   • KPI stat grid — 5 cards using the anchor's `Stat` primitive
 *     shape; `text-[26px] tabular-nums font-semibold`, first card
 *     (Total) highlighted with `--ds-color-accent-strong`.
 *   • `UnderExtractionHealthCard` — the traffic-light thresholds
 *     (7d / 30d) are preserved BYTE-EXACT; only the color VALUES are
 *     rewired via a `HEALTH_TONE` map onto muted DS tokens (success-
 *     muted / warning / danger / neutral-muted). No hex-with-alpha
 *     string concatenation (was `${accent}12` / `${accent}33` /
 *     `${accent}55` — replaced with pre-tokenised soft/border pairs).
 *   • STATUS_COLOR pastel map REMOVED. `Pill` now emits a DS `Badge`
 *     with `shape="sharp"` + `STATUS_TONE` map: success → successMuted,
 *     partial → warning, failed → danger, pending → neutralMuted. All
 *     SEMANTIC KEYS preserved byte-exact; only visuals rewired.
 *   • Events table — dense `px-4 py-1.5` rows with
 *     `borderBottom: 1px solid var(--ds-color-admin-border)` +
 *     `isLast` guard, `overflow-x-auto`, `tabular-nums font-mono` on
 *     all numeric cells.
 *   • Pagination — admin chrome, 3px radii, dense padding.
 *   • Row-detail modal — hairline chrome, sharp radii, admin-token
 *     meta grid; error / under-extraction call-outs on
 *     `--ds-color-danger-soft` + `--ds-color-danger`.
 *
 * Business logic (fetch + filter + debounced report_id search +
 * pagination + blob view lazy-load + audit-trail wording) preserved
 * BYTE-EXACT. All testids unchanged. Threshold values (7 / 30 days)
 * unchanged. FCRA compliance copy in AdminPageHelp unchanged.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  ArrowLeft,
  Download,
  Filter,
  RefreshCw,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { Heading } from "@/design-system/components/Heading";
import { Badge } from "@/design-system/components/Badge";
import { GhostButton, Section, SelectInput, Stat, TextInput } from "@/admin/_primitives";

/* ------------------------------------------------------------------ */
/* Status pill → DS Badge tone map (semantic keys byte-exact).          */
/* ------------------------------------------------------------------ */

const STATUS_TONE = {
  success: "successMuted",
  partial: "warning",
  failed: "danger",
  pending: "neutralMuted",
};

const STATUS_OPTIONS = ["", "pending", "success", "partial", "failed"];
const SOURCE_OPTIONS = ["", "pdf_upload", "idiq_api", "idiq_pdf", "legacy_backfill"];

/* Traffic-light health card tone map. Threshold values (7 / 30 days)
   below in `UnderExtractionHealthCard` are preserved BYTE-EXACT. */
const HEALTH_TONE = {
  green: {
    bg: "var(--ds-color-success-muted-soft)",
    border: "var(--ds-color-success-muted)",
    ink: "var(--ds-color-success-muted)",
  },
  amber: {
    bg: "var(--ds-color-warning-soft)",
    border: "var(--ds-color-warning)",
    ink: "var(--ds-color-warning)",
  },
  red: {
    bg: "var(--ds-color-danger-soft)",
    border: "var(--ds-color-danger)",
    ink: "var(--ds-color-danger)",
  },
  grey: {
    bg: "var(--ds-color-neutral-muted-soft)",
    border: "var(--ds-color-admin-border)",
    ink: "var(--ds-color-neutral-muted)",
  },
};

/* ------------------------------------------------------------------ */
/* Local admin primitives (mirror anchor).                              */
/* ------------------------------------------------------------------ */






/* ------------------------------------------------------------------ */
/* Status Pill                                                          */
/* ------------------------------------------------------------------ */

function Pill({ value }) {
  if (!value) {
    return <span style={{ color: "var(--ds-color-ink-subtle)" }}>—</span>;
  }
  return (
    <Badge
      tone={STATUS_TONE[value] || "neutralMuted"}
      size="sm"
      shape="sharp"
      data-testid={`credit-engine-status-pill-${value}`}
      className="uppercase tracking-[0.14em]"
    >
      {String(value).replace(/_/g, " ")}
    </Badge>
  );
}

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function AdminCreditEngineEvents() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [reportIdFilter, setReportIdFilter] = useState("");
  const [debouncedReportId, setDebouncedReportId] = useState("");
  const [offset, setOffset] = useState(0);
  const [openEventId, setOpenEventId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedReportId(reportIdFilter.trim()), 250);
    return () => clearTimeout(t);
  }, [reportIdFilter]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (sourceFilter) params.set("source", sourceFilter);
      if (debouncedReportId) params.set("report_id", debouncedReportId);
      params.set("limit", "50");
      params.set("offset", String(offset));
      const [{ data }, { data: statsData }, { data: healthData }] = await Promise.all([
        api.get(`/admin/credit-engine/events?${params.toString()}`),
        api.get(`/admin/credit-engine/events-stats`),
        api.get(`/admin/credit-engine/health`),
      ]);
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      setStats(statsData || null);
      setHealth(healthData || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, debouncedReportId, offset]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filterBadge = (
    <GhostButton
      onClick={reload}
      data-testid="credit-engine-events-refresh"
      className="!py-1 !text-[11px]"
    >
      <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
    </GhostButton>
  );

  return (
    <AdminLayout
      title="Credit engine events"
      subtitle={`${total} rows in credit_engine_events${stats ? ` · ${stats.since_midnight_utc} since 00:00 UTC` : ""}`}
    >
      <div className="mb-3 max-w-3xl" data-testid="credit-engine-events-help">
        <AdminPageHelp
          title="Credit engine events"
          purpose="V2.1 Track 1 (Phase 0.5) observability dashboard. Every credit-report import (pdf_upload, idiq_api, idiq_pdf, legacy_backfill) writes exactly one event row so you have a full audit trail of what the extractor did, how many accounts it declared vs. extracted, whether under-extraction fired, the model used, and the raw LLM prompt + completion for forensic review."
          configures="Nothing — this page is read-only. Use it to spot under-extraction patterns, model tier switches, or repeated failures before the V2.1 rewrite lands."
          impact="No user-facing impact. Records retained per collection TTL. Raw prompt/completion blobs live in GridFS bucket credit_engine_blobs."
          safety="Read-only. Every open + every raw-blob view is written to admin_audit_logs (FCRA compliance for operations staff access)."
        />
      </div>

      <UnderExtractionHealthCard health={health} />

      {stats && (
        <div
          data-testid="credit-engine-events-stats-row"
          className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3"
        >
          {["total", "success", "partial", "failed", "pending"].map((k) => {
            const v = k === "total" ? stats.total : stats.by_status?.[k] || 0;
            return (
              <Stat
                key={k}
                testid={`credit-engine-stat-${k}`}
                label={k}
                value={v}
                highlight={k === "total"}
              />
            );
          })}
        </div>
      )}

      <Section title="Filter" icon={Filter} badge={filterBadge} testid="credit-engine-events-filters">
        <div className="grid md:grid-cols-[1fr_repeat(2,auto)] gap-3 items-end">
          <label className="block">
            <span
              className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Report ID
            </span>
            <div className="relative">
              <Search
                className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2"
                style={{ color: "var(--ds-color-ink-subtle)" }}
                aria-hidden="true"
              />
              <TextInput
                data-testid="credit-engine-events-filter-report-id"
                type="text"
                value={reportIdFilter}
                onChange={(e) => {
                  setOffset(0);
                  setReportIdFilter(e.target.value);
                }}
                placeholder="Filter by report_id…"
                className="w-full pl-8"
              />
            </div>
          </label>
          <label className="block">
            <span
              className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Status
            </span>
            <SelectInput
              data-testid="credit-engine-events-filter-status"
              value={statusFilter}
              onChange={(e) => {
                setOffset(0);
                setStatusFilter(e.target.value);
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s || "any-status"} value={s}>
                  {s || "All statuses"}
                </option>
              ))}
            </SelectInput>
          </label>
          <label className="block">
            <span
              className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Source
            </span>
            <SelectInput
              data-testid="credit-engine-events-filter-source"
              value={sourceFilter}
              onChange={(e) => {
                setOffset(0);
                setSourceFilter(e.target.value);
              }}
            >
              {SOURCE_OPTIONS.map((s) => (
                <option key={s || "any-source"} value={s}>
                  {s || "All sources"}
                </option>
              ))}
            </SelectInput>
          </label>
        </div>
      </Section>

      {loading ? (
        <div
          data-testid="credit-engine-events-loading"
          className="text-[13px] py-6 text-center"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          Loading…
        </div>
      ) : (
        <Section
          title="Events feed"
          icon={Activity}
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {items.length} showing · {total} total
            </span>
          }
          testid="credit-engine-events-table-section"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto" data-testid="credit-engine-events-table">
            <table className="w-full text-[13px]">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2 font-semibold">When</th>
                  <th className="px-4 py-2 font-semibold">Source</th>
                  <th className="px-4 py-2 font-semibold">Filename</th>
                  <th className="px-4 py-2 font-semibold">Model</th>
                  <th className="px-4 py-2 font-semibold text-right">Pages</th>
                  <th className="px-4 py-2 font-semibold text-right">Accts (extr/decl)</th>
                  <th className="px-4 py-2 font-semibold text-right">Neg</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => {
                  const isLast = idx === items.length - 1;
                  return (
                    <tr
                      key={row.event_id}
                      data-testid={`credit-engine-events-row-${row.event_id}`}
                      className="cursor-pointer transition-colors"
                      onClick={() => setOpenEventId(row.event_id)}
                      style={{
                        borderBottom: isLast
                          ? "none"
                          : "1px solid var(--ds-color-admin-border)",
                        transitionDuration: "var(--ds-duration-fast)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "var(--ds-color-admin-surface-muted)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td
                        className="px-4 py-1.5 whitespace-nowrap text-[12px] tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.created_at
                          ? String(row.created_at).slice(0, 19).replace("T", " ")
                          : "—"}
                      </td>
                      <td className="px-4 py-1.5 text-[12px]" style={{ color: "var(--ds-color-ink)" }}>
                        {row.source}
                        {row.under_extraction_flag && (
                          <div
                            data-testid={`credit-engine-under-extraction-${row.event_id}`}
                            className="text-[9px] uppercase tracking-[0.12em] mt-0.5 font-semibold"
                            style={{ color: "var(--ds-color-danger)" }}
                          >
                            under-extracted
                          </div>
                        )}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] truncate max-w-[180px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.filename || "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] font-mono truncate max-w-[160px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.ai_model_used || "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.page_count_declared ?? "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.accounts_extracted ?? "—"}/{row.accounts_declared ?? "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {(row.charge_offs_detected ?? 0) +
                          (row.collections_detected ?? 0) +
                          (row.late_payments_detected ?? 0)}
                      </td>
                      <td className="px-4 py-1.5">
                        <Pill value={row.status} />
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        <span
                          className="text-[11px] uppercase tracking-[0.12em] font-semibold"
                          style={{ color: "var(--ds-color-accent-strong)" }}
                        >
                          Open
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No credit engine events match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <div
        className="flex items-center justify-between mt-2 text-[11px] tabular-nums"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        <span>Page offset {offset}</span>
        <div className="flex gap-1.5">
          <GhostButton
            data-testid="credit-engine-events-prev-page"
            onClick={() => setOffset(Math.max(0, offset - 50))}
            disabled={offset === 0}
            className="!py-1 !text-[11px]"
          >
            Prev
          </GhostButton>
          <GhostButton
            data-testid="credit-engine-events-next-page"
            onClick={() => setOffset(offset + 50)}
            disabled={offset + 50 >= total}
            className="!py-1 !text-[11px]"
          >
            Next
          </GhostButton>
        </div>
      </div>

      {openEventId && (
        <CreditEngineEventDetail
          eventId={openEventId}
          onClose={() => setOpenEventId(null)}
        />
      )}
    </AdminLayout>
  );
}

/* ------------------------------------------------------------------ */
/* Row-detail modal                                                     */
/* ------------------------------------------------------------------ */

function CreditEngineEventDetail({ eventId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rawBody, setRawBody] = useState(null);
  const [rawKind, setRawKind] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/admin/credit-engine/events/${eventId}`);
        if (!cancel) setDetail(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [eventId]);

  const loadBlob = useCallback(
    async (kind) => {
      setRawKind(kind);
      setRawBody(null);
      setRawLoading(true);
      try {
        const { data } = await api.get(
          `/admin/credit-engine/events/${eventId}/blob/${kind}`,
          {
            transformResponse: [(v) => v], // keep as plain text
          },
        );
        setRawBody(typeof data === "string" ? data : String(data));
      } catch (e) {
        toast.error(formatApiError(e));
        setRawBody(null);
      } finally {
        setRawLoading(false);
      }
    },
    [eventId],
  );

  const promptRef = detail?.raw_prompt_ref || {};
  const completionRef = detail?.raw_completion_ref || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(27, 26, 22, 0.55)" }}
      onClick={onClose}
      data-testid="credit-engine-events-modal-backdrop"
    >
      <div
        data-testid="credit-engine-events-modal"
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-[var(--ds-radius-card)]"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="sticky top-0 flex items-center justify-between px-4 py-2.5 z-10 border-b"
          style={{
            background: "var(--ds-color-surface)",
            borderColor: "var(--ds-color-admin-border)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="credit-engine-events-modal-back"
              className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.12em] transition-colors"
              style={{ color: "var(--ds-color-ink-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div
                className="text-[10px] uppercase tracking-[0.16em] font-semibold"
                style={{ color: "var(--ds-color-accent-strong)" }}
              >
                Credit engine event
              </div>
              <Heading level={4} size="sm" weight="semibold" className="truncate leading-none mt-0.5">
                {detail?.filename || eventId}
              </Heading>
            </div>
          </div>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: "var(--ds-color-ink-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            data-testid="credit-engine-events-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-4 space-y-3">
          {loading && (
            <div
              className="text-[13px]"
              style={{ color: "var(--ds-color-ink-muted)" }}
              data-testid="credit-engine-events-modal-loading"
            >
              Loading…
            </div>
          )}
          {detail && (
            <>
              <div
                className="grid grid-cols-2 md:grid-cols-3 gap-3"
                data-testid="credit-engine-events-modal-meta"
              >
                <Meta label="Event id" value={detail.event_id} mono />
                <Meta label="Report id" value={detail.report_id} mono />
                <Meta label="User (masked)" value={detail.user_id_masked} mono />
                <Meta label="Source" value={detail.source} />
                <Meta label="Status" value={detail.status} />
                <Meta label="Model" value={detail.ai_model_used} />
                <Meta label="Parser version" value={detail.parser_version} />
                <Meta label="Rules engine" value={detail.rules_engine_version} />
                <Meta
                  label="Duration"
                  value={
                    detail.processing_duration_ms != null
                      ? `${detail.processing_duration_ms} ms`
                      : "—"
                  }
                />
                <Meta
                  label="Pages (declared/OCR)"
                  value={`${detail.page_count_declared ?? "—"} / ${detail.page_count_ocr ?? "—"}`}
                />
                <Meta
                  label="OCR confidence"
                  value={detail.ocr_confidence != null ? detail.ocr_confidence.toFixed(2) : "—"}
                />
                <Meta label="Retry attempts" value={detail.retry_attempts ?? "—"} />
                <Meta
                  label="Accounts (extr/decl)"
                  value={`${detail.accounts_extracted ?? "—"} / ${detail.accounts_declared ?? "—"}`}
                />
                <Meta label="Charge-offs" value={detail.charge_offs_detected ?? "—"} />
                <Meta label="Collections" value={detail.collections_detected ?? "—"} />
                <Meta label="Late payments" value={detail.late_payments_detected ?? "—"} />
                <Meta label="Inquiries" value={detail.inquiries_detected ?? "—"} />
                <Meta label="Public records" value={detail.public_records_detected ?? "—"} />
              </div>

              {detail.under_extraction_flag && (
                <div
                  data-testid="credit-engine-under-extraction-detail"
                  className="rounded-[var(--ds-radius-input)] p-3 text-[12px]"
                  style={{
                    background: "var(--ds-color-danger-soft)",
                    border: "1px solid var(--ds-color-danger)",
                    color: "var(--ds-color-danger)",
                  }}
                >
                  Under-extraction flagged: {detail.under_extraction_reason || "(no reason captured)"}
                </div>
              )}

              {detail.error_summary && (
                <div>
                  <div
                    className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                    style={{ color: "var(--ds-color-ink-subtle)" }}
                  >
                    Error
                  </div>
                  <div
                    className="rounded-[var(--ds-radius-input)] p-2 text-[12px]"
                    style={{
                      background: "var(--ds-color-danger-soft)",
                      border: "1px solid var(--ds-color-danger)",
                      color: "var(--ds-color-danger)",
                    }}
                  >
                    {detail.error_summary}
                  </div>
                </div>
              )}

              <div>
                <div
                  className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-2"
                  style={{ color: "var(--ds-color-accent-strong)" }}
                >
                  Raw payloads
                </div>
                <div className="flex flex-wrap gap-2">
                  <GhostButton
                    data-testid="credit-engine-events-view-prompt"
                    onClick={() => loadBlob("prompt")}
                    disabled={!promptRef.gridfs_id}
                  >
                    <Download className="w-3 h-3" /> View prompt
                    {promptRef.length_bytes
                      ? ` (${promptRef.length_bytes.toLocaleString()} B)`
                      : ""}
                  </GhostButton>
                  <GhostButton
                    data-testid="credit-engine-events-view-completion"
                    onClick={() => loadBlob("completion")}
                    disabled={!completionRef.gridfs_id}
                  >
                    <Download className="w-3 h-3" /> View completion
                    {completionRef.length_bytes
                      ? ` (${completionRef.length_bytes.toLocaleString()} B)`
                      : ""}
                  </GhostButton>
                </div>
                {rawKind && (
                  <div className="mt-3">
                    <div
                      className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                      style={{ color: "var(--ds-color-ink-subtle)" }}
                    >
                      Raw {rawKind}
                    </div>
                    <pre
                      data-testid={`credit-engine-events-raw-${rawKind}`}
                      className="rounded-[var(--ds-radius-input)] p-3 text-[11px] whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-y-auto font-mono"
                      style={{
                        background: "var(--ds-color-admin-surface-muted)",
                        border: "1px solid var(--ds-color-admin-border)",
                        color: "var(--ds-color-ink-muted)",
                      }}
                    >
                      {rawLoading ? "Loading…" : rawBody || "(empty)"}
                    </pre>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, mono = false }) {
  return (
    <div>
      <div
        className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div
        className={`text-[13px] ${mono ? "font-mono text-[11px] break-all" : ""}`}
        style={{ color: "var(--ds-color-ink)" }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Under-extraction health card                                         */
/* Threshold values (7d / 30d) preserved BYTE-EXACT.                    */
/* ------------------------------------------------------------------ */

function UnderExtractionHealthCard({ health }) {
  if (!health) return null;
  const {
    days_since_last_under_extraction: days,
    total_under_extractions_ever: total,
    under_extractions_last_7d: d7,
    under_extractions_last_30d: d30,
    latest_under_extraction: latest,
    data_window_start: windowStart,
  } = health;

  const hasEver = total > 0 && latest;

  // Traffic-light-ish: green if >30 days, amber if 7-30, red if <7,
  // grey if no data. Threshold values UNCHANGED — only tone map rewired.
  let toneKey = "green";
  if (!hasEver) toneKey = "grey";
  else if (days != null && days < 7) toneKey = "red";
  else if (days != null && days < 30) toneKey = "amber";
  const tone = HEALTH_TONE[toneKey];

  return (
    <section
      data-testid="credit-engine-under-extraction-health-card"
      className="mb-3 rounded-[var(--ds-radius-card)] overflow-hidden"
      style={{
        background: "var(--ds-color-surface)",
        border: `1px solid ${tone.border}`,
        boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
      }}
    >
      <div className="grid md:grid-cols-[auto_1fr_auto] items-stretch">
        {/* Left rail — headline number on the tone-tinted surface */}
        <div
          className="px-4 py-3 md:min-w-[220px]"
          style={{
            background: tone.bg,
            borderRight: `1px solid ${tone.border}`,
          }}
        >
          <div
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: tone.ink }}
          >
            <ShieldAlert className="w-3 h-3" aria-hidden="true" />
            Under-extraction health
          </div>
          {hasEver ? (
            <>
              <div
                data-testid="credit-engine-days-since-value"
                className="mt-1 text-[32px] leading-tight tabular-nums font-semibold"
                style={{ color: "var(--ds-color-ink)" }}
              >
                {days != null ? days.toFixed(days < 1 ? 2 : 1) : "—"}
              </div>
              <div
                className="text-[11px] mt-0.5"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                days since last under-extraction
              </div>
            </>
          ) : (
            <>
              <div
                data-testid="credit-engine-days-since-value"
                className="mt-1 text-[22px] leading-tight font-semibold"
                style={{ color: "var(--ds-color-ink)" }}
              >
                Clean
              </div>
              <div
                data-testid="credit-engine-no-under-extraction-message"
                className="text-[11px] mt-0.5"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                No under-extractions detected in the current data window.
              </div>
            </>
          )}
        </div>

        {/* Middle — rolling counts */}
        <div className="px-4 py-3 flex items-center gap-8">
          <div>
            <div
              className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Last 7 days
            </div>
            <div
              data-testid="credit-engine-under-extraction-7d"
              className="text-[22px] tabular-nums font-semibold"
              style={{ color: "var(--ds-color-ink)" }}
            >
              {d7 ?? 0}
            </div>
          </div>
          <div>
            <div
              className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Last 30 days
            </div>
            <div
              data-testid="credit-engine-under-extraction-30d"
              className="text-[22px] tabular-nums font-semibold"
              style={{ color: "var(--ds-color-ink)" }}
            >
              {d30 ?? 0}
            </div>
          </div>
          <div>
            <div
              className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              All-time
            </div>
            <div
              data-testid="credit-engine-under-extraction-total"
              className="text-[22px] tabular-nums font-semibold"
              style={{ color: "var(--ds-color-ink)" }}
            >
              {total ?? 0}
            </div>
          </div>
        </div>

        {/* Right — latest event pointer */}
        <div
          className="px-4 py-3 md:text-right md:min-w-[260px] text-[11px]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {hasEver ? (
            <>
              <div
                className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Latest incident
              </div>
              <div
                data-testid="credit-engine-under-extraction-latest-model"
                className="font-mono text-[11px] truncate"
                style={{ color: "var(--ds-color-ink)" }}
              >
                {latest?.ai_model_used || "—"}
              </div>
              <div
                data-testid="credit-engine-under-extraction-latest-when"
                className="mt-0.5 tabular-nums"
              >
                {latest?.created_at
                  ? new Date(latest.created_at).toISOString().slice(0, 19).replace("T", " ") + " UTC"
                  : "—"}
              </div>
              <div className="mt-0.5 truncate" title={latest?.under_extraction_reason || ""}>
                {latest?.under_extraction_reason || "no reason recorded"}
              </div>
            </>
          ) : (
            <>
              <div
                className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Data window
              </div>
              <div style={{ color: "var(--ds-color-ink)" }}>
                {windowStart ? windowStart.slice(0, 10) : "—"} → now
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
